import React, { useEffect, useRef } from 'react';
import { Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { SampleDataBadge } from '../../common';

// Register Chart.js components
ChartJS.register(...registerables, ChartDataLabels);

// Expand icon
const ExpandIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
            d="M4 16V12M4 16H8M4 16L9 11M16 4V8M16 4H12M16 4L11 9"
            stroke="#666666"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * Year-wise Distribution Chart
 * Grouped bar chart showing Students Involved and Projects by year
 */
const MBFYearWiseChart = ({ data = {}, isSampleData = false, onMaximize, isFullscreen = false }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Default data
    const years = ['1st Year', '2nd Year', '3rd Year', '4th Year'];
    const defaultStudentsInvolved = [160, 180, 200, 120];
    const defaultProjects = [80, 100, 140, 60];

    const studentsInvolved = data.students || defaultStudentsInvolved;
    const projects = data.projects || defaultProjects;

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const ctx = chartRef.current.getContext('2d');
            const fontSize = isMobile ? 10 : 12;
            const barThickness = isMobile ? 20 : 35;

            chartInstance.current = new ChartJS(ctx, {
                type: 'bar',
                data: {
                    labels: years,
                    datasets: [
                        {
                            label: 'Students Involved',
                            data: studentsInvolved,
                            backgroundColor: '#E0F5FF',
                            borderColor: '#0C597D',
                            borderWidth: 1,
                            borderRadius: 4,
                            barThickness: barThickness,
                        },
                        {
                            label: 'Projects',
                            data: projects,
                            backgroundColor: '#FFF7D9',
                            borderColor: '#A4881A',
                            borderWidth: 1,
                            borderRadius: 4,
                            barThickness: barThickness,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            titleColor: '#333333',
                            bodyColor: '#333333',
                            borderColor: '#e6e6e6',
                            borderWidth: 1,
                            padding: 10,
                            cornerRadius: 8,
                        },
                        datalabels: {
                            display: false,
                        },
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            border: { display: false },
                            ticks: {
                                color: '#222222',
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: fontSize,
                                },
                            },
                        },
                        y: {
                            min: 0,
                            max: 320,
                            ticks: {
                                stepSize: 80,
                                color: '#444444',
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: fontSize,
                                },
                            },
                            border: { display: false },
                            grid: { color: '#f0f0f0' },
                        },
                    },
                },
            });
        }

        return () => {
            if (chartInstance.current) chartInstance.current.destroy();
        };
    }, [isMobile, studentsInvolved, projects]);

    // Fullscreen mode - render just the chart without wrapper
    if (isFullscreen) {
        return (
            <Box sx={{ width: '100%', height: '100%', p: 2 }}>
                <canvas ref={chartRef} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e6e6e6',
                display: 'flex',
                flexDirection: 'column',
                height: '408px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    bgcolor: '#ffffff',
                    borderBottom: '1px solid #e6e6e6',
                    padding: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                        sx={{
                            color: '#333333',
                            fontFamily: '"Roboto", sans-serif',
                            fontSize: '16px',
                            lineHeight: '20px',
                            fontWeight: 500,
                        }}
                    >
                        Year-wise Distribution
                    </Typography>
                    {isSampleData && <SampleDataBadge absolute={false} />}
                </Box>

                <IconButton
                    size="small"
                    onClick={onMaximize}
                    sx={{
                        bgcolor: '#ffffff',
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        padding: '8px',
                        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.05)',
                        '&:hover': { bgcolor: '#f9f9f9' },
                    }}
                >
                    <ExpandIcon />
                </IconButton>
            </Box>

            {/* Content Area */}
            <Box
                sx={{
                    bgcolor: '#fafafa',
                    padding: '12px',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }}
            >
                {/* Chart Container */}
                <Box
                    sx={{
                        bgcolor: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e6e6e6',
                        padding: '12px',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '8px',
                        minHeight: 0,
                        position: 'relative',
                    }}
                >
                    {/* Legend - Top Right */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '12px',
                            right: '12px',
                            display: 'flex',
                            gap: '12px',
                            zIndex: 1,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <Box
                                sx={{
                                    width: '26px',
                                    height: '13px',
                                    bgcolor: '#E0F5FF',
                                    border: '1px solid #0C597D',
                                    borderRadius: '2px',
                                }}
                            />
                            <Typography
                                sx={{
                                    color: '#333333',
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: '13px',
                                }}
                            >
                                Students Involved
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <Box
                                sx={{
                                    width: '26px',
                                    height: '13px',
                                    bgcolor: '#FFF7D9',
                                    border: '1px solid #A4881A',
                                    borderRadius: '2px',
                                }}
                            />
                            <Typography
                                sx={{
                                    color: '#333333',
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: '13px',
                                }}
                            >
                                Projects
                            </Typography>
                        </Box>
                    </Box>

                    {/* Y-Axis Label */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            writingMode: 'vertical-rl',
                            transform: 'rotate(180deg)',
                        }}
                    >
                        <Typography
                            sx={{
                                color: '#333333',
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '14px',
                                fontWeight: 500,
                            }}
                        >
                            No. of Users
                        </Typography>
                    </Box>

                    {/* Chart + X-axis Label */}
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            minHeight: 0,
                        }}
                    >
                        {/* Chart */}
                        <Box sx={{ flex: 1, minHeight: 0, pt: 3 }}>
                            <canvas ref={chartRef} />
                        </Box>

                        {/* X-Axis Label */}
                        <Typography
                            sx={{
                                color: '#333333',
                                fontFamily: '"Inter", sans-serif',
                                fontSize: '14px',
                                fontWeight: 500,
                                textAlign: 'center',
                            }}
                        >
                            Years
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default MBFYearWiseChart;
